import React, { useState, useEffect } from 'react';
import {
  Calendar, FileText, Image, Plus, Edit2, Trash2, Send, Search,
  ChevronDown, ChevronRight, Clock, CheckCircle, XCircle, Link2,
  Download, Upload, Grid, List, Eye, Copy, ExternalLink, Share2,
  Mail, Globe, TrendingUp, Palette, Settings
} from 'lucide-react';
import axios from 'axios';

// Type-specific asset creation components
import SocialAssetCreation from '../assets/by-type/SocialAssetCreation';
import WebsiteAssetCreation from '../assets/by-type/WebsiteAssetCreation';
import SEOAssetCreation from '../assets/by-type/SEOAssetCreation';
import AdsAssetCreation from '../assets/by-type/AdsAssetCreation';
import EmailAssetCreation from '../assets/by-type/EmailAssetCreation';
import DesignAssetCreation from '../assets/by-type/DesignAssetCreation';
import CustomAssetCreation from '../assets/by-type/CustomAssetCreation';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface Plan {
  id: number;
  type: 'weekly' | 'monthly';
  name: string;
  start_date: string;
  end_date: string;
  status: 'draft' | 'active' | 'completed' | 'archived';
  client_id?: number;
  client_name?: string;
  items?: ContentItem[];
  item_count?: number;
  approval_status?: string;
}

interface ContentItem {
  id: number;
  type: string;
  title: string;
  description?: string;
  status: string;
  client_id?: number;
  plan_id?: number;
  created_at: string;
  assets?: Asset[];
  asset_count?: number;
}

interface Asset {
  id: number;
  content_id?: number;
  asset_type: string;
  file_name: string;
  file_size?: number;
  mime_type?: string;
  r2_key: string;
  created_at: string;
}

interface Client {
  id: number;
  name: string;
}

type ContentType = 'social' | 'website' | 'seo' | 'ads' | 'email' | 'design' | 'custom';

export default function ContentManagement() {
  const [activeTab, setActiveTab] = useState<'plans' | 'content' | 'assets'>('plans');
  const [activeContentType, setActiveContentType] = useState<ContentType>('social');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedPlans, setExpandedPlans] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  // Modal states
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [showGenerateLinksModal, setShowGenerateLinksModal] = useState(false);
  const [showAssetCreation, setShowAssetCreation] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [editingContent, setEditingContent] = useState<ContentItem | null>(null);
  const [selectedPlanForLinks, setSelectedPlanForLinks] = useState<Plan | null>(null);

  // Form data
  const [planForm, setPlanForm] = useState({
    name: '',
    type: 'monthly' as 'weekly' | 'monthly',
    start_date: '',
    end_date: '',
    client_id: '',
    status: 'draft' as Plan['status']
  });

  const [contentForm, setContentForm] = useState({
    title: '',
    type: 'website',
    description: '',
    status: 'draft',
    client_id: '',
    plan_id: ''
  });

  useEffect(() => {
    // Set auth token from localStorage
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load clients for dropdowns
      const clientsRes = await axios.get(`${API_BASE_URL}/clients`);
      setClients(clientsRes.data);

      // Load data based on active tab
      if (activeTab === 'plans') {
        await loadPlans();
      } else if (activeTab === 'content') {
        await loadContentItems();
      } else if (activeTab === 'assets') {
        await loadAssets();
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/plans`);
      const plansData = response.data;

      // Load content items for each plan
      const plansWithItems = await Promise.all(
        plansData.map(async (plan: Plan) => {
          try {
            const itemsRes = await axios.get(`${API_BASE_URL}/content?plan_id=${plan.id}`);
            return {
              ...plan,
              items: itemsRes.data,
              item_count: itemsRes.data.length
            };
          } catch {
            return { ...plan, items: [], item_count: 0 };
          }
        })
      );

      setPlans(plansWithItems);
    } catch (error) {
      console.error('Failed to load plans:', error);
    }
  };

  const loadContentItems = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/content`);
      const contentData = Array.isArray(response.data) ? response.data : [];

      // Load assets count for each content item
      const contentWithAssets = await Promise.all(
        contentData.map(async (item: ContentItem) => {
          try {
            const assetsRes = await axios.get(`${API_BASE_URL}/assets?content_id=${item.id}`);
            const assets = Array.isArray(assetsRes.data) ? assetsRes.data : [];
            return {
              ...item,
              assets: assets,
              asset_count: assets.length
            };
          } catch {
            return { ...item, assets: [], asset_count: 0 };
          }
        })
      );

      setContentItems(contentWithAssets);
    } catch (error) {
      console.error('Failed to load content items:', error);
      setContentItems([]);
    }
  };

  const loadAssets = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/assets`);
      const assetsData = Array.isArray(response.data) ? response.data : [];
      setAssets(assetsData);
    } catch (error) {
      console.error('Failed to load assets:', error);
      setAssets([]);
    }
  };

  const handleSavePlan = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingPlan) {
        await axios.put(`${API_BASE_URL}/plans/${editingPlan.id}`, planForm);
      } else {
        await axios.post(`${API_BASE_URL}/plans`, planForm);
      }

      await loadPlans();
      handleClosePlanModal();
    } catch (error) {
      console.error('Failed to save plan:', error);
      alert('Failed to save plan');
    }
  };

  const handleSaveContent = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingContent) {
        await axios.put(`${API_BASE_URL}/content/${editingContent.id}`, contentForm);
      } else {
        await axios.post(`${API_BASE_URL}/content`, contentForm);
      }

      if (activeTab === 'plans') {
        await loadPlans();
      } else {
        await loadContentItems();
      }
      handleCloseContentModal();
    } catch (error) {
      console.error('Failed to save content:', error);
      alert('Failed to save content');
    }
  };

  const handleGenerateLinks = async () => {
    if (!selectedPlanForLinks) return;

    try {
      const response = await axios.post(`${API_BASE_URL}/review/generate-links`, {
        plan_id: selectedPlanForLinks.id
      });

      alert(
        `Review links generated successfully!\n\n` +
        `Account Manager Link:\n${response.data.account_manager_link}\n\n` +
        `Client Link:\n${response.data.client_link}`
      );

      setShowGenerateLinksModal(false);
      setSelectedPlanForLinks(null);
    } catch (error) {
      console.error('Failed to generate links:', error);
      alert('Failed to generate review links');
    }
  };

  const handleDeletePlan = async (planId: number) => {
    if (confirm('Are you sure you want to delete this plan?')) {
      try {
        await axios.delete(`${API_BASE_URL}/plans/${planId}`);
        await loadPlans();
      } catch (error) {
        console.error('Failed to delete plan:', error);
        alert('Failed to delete plan');
      }
    }
  };

  const handleDeleteContent = async (contentId: number) => {
    if (confirm('Are you sure you want to delete this content item?')) {
      try {
        await axios.delete(`${API_BASE_URL}/content/${contentId}`);
        if (activeTab === 'plans') {
          await loadPlans();
        } else {
          await loadContentItems();
        }
      } catch (error) {
        console.error('Failed to delete content:', error);
        alert('Failed to delete content');
      }
    }
  };

  const handleDeleteAsset = async (assetId: number) => {
    if (confirm('Are you sure you want to delete this asset?')) {
      try {
        await axios.delete(`${API_BASE_URL}/assets/${assetId}`);
        await loadAssets();
      } catch (error) {
        console.error('Failed to delete asset:', error);
        alert('Failed to delete asset');
      }
    }
  };

  const togglePlanExpansion = (planId: number) => {
    const newExpanded = new Set(expandedPlans);
    if (newExpanded.has(planId)) {
      newExpanded.delete(planId);
    } else {
      newExpanded.add(planId);
    }
    setExpandedPlans(newExpanded);
  };

  const handleClosePlanModal = () => {
    setShowPlanModal(false);
    setEditingPlan(null);
    setPlanForm({
      name: '',
      type: 'monthly',
      start_date: '',
      end_date: '',
      client_id: '',
      status: 'draft'
    });
  };

  const handleCloseContentModal = () => {
    setShowContentModal(false);
    setEditingContent(null);
    setContentForm({
      title: '',
      type: 'website',
      description: '',
      status: 'draft',
      client_id: '',
      plan_id: ''
    });
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-700',
      active: 'bg-green-100 text-green-700',
      pending_review: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      completed: 'bg-blue-100 text-blue-700',
      rejected: 'bg-red-100 text-red-700',
      archived: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getApprovalIcon = (status?: string) => {
    if (!status) return <Clock className="w-4 h-4 text-gray-400" />;
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  // Filter data based on search and filters
  const filterData = (data: any[]) => {
    let filtered = [...data];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(query) ||
        item.title?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.file_name?.toLowerCase().includes(query)
      );
    }

    if (selectedClient !== 'all') {
      filtered = filtered.filter(item => item.client_id === parseInt(selectedClient));
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(item => item.status === selectedStatus);
    }

    return filtered;
  };

  // Additional filter for content type
  const filterContentByType = (data: ContentItem[]) => {
    let filtered = filterData(data);

    // Filter by active content type
    if (activeTab === 'content') {
      filtered = filtered.filter(item => {
        // Map content types to match the activeContentType
        const itemType = item.type.toLowerCase();

        // Handle content type matching
        if (activeContentType === 'social' && (itemType === 'social' || itemType === 'social_media')) {
          return true;
        } else if (activeContentType === 'website' && itemType === 'website') {
          return true;
        } else if (activeContentType === 'seo' && itemType === 'seo') {
          return true;
        } else if (activeContentType === 'ads' && (itemType === 'ads' || itemType.includes('_ads'))) {
          return true;
        } else if (activeContentType === 'email' && itemType === 'email') {
          return true;
        } else if (activeContentType === 'design' && itemType === 'design') {
          return true;
        } else if (activeContentType === 'custom' && itemType === 'custom') {
          return true;
        }
        return false;
      });
    }

    return filtered;
  };

  const filteredPlans = filterData(plans);
  const filteredContent = filterContentByType(contentItems);
  const filteredAssets = filterData(assets);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading content...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Content Management</h1>
        <p className="text-gray-600 mt-1">Manage plans, content items, and assets in one place</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('plans')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'plans'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                Plans ({plans.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'content'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <FileText className="w-4 h-4 mr-2" />
                Content Items ({contentItems.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('assets')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'assets'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <Image className="w-4 h-4 mr-2" />
                Assets ({assets.length})
              </div>
            </button>
          </nav>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Search ${activeTab}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </div>

            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              <option value="all">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="pending_review">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>

            {activeTab === 'assets' && (
              <div className="flex space-x-2">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <List className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Grid className="w-5 h-5" />
                </button>
              </div>
            )}

            <button
              onClick={() => {
                if (activeTab === 'plans') setShowPlanModal(true);
                else if (activeTab === 'content') setShowAssetCreation(true);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              <Plus className="w-5 h-5 mr-2" />
              {activeTab === 'plans' ? 'Add Plan' : activeTab === 'content' ? `Create ${activeContentType.charAt(0).toUpperCase() + activeContentType.slice(1)} Content` : 'Upload Asset'}
            </button>
          </div>
        </div>

        {/* Content Type Tabs (visible when content tab is active) */}
        {activeTab === 'content' && (
          <div className="border-b border-gray-200 bg-gray-50">
            <nav className="-mb-px flex space-x-4 px-6 py-2 overflow-x-auto">
              {[
                { id: 'social', name: 'Social Media', icon: Share2 },
                { id: 'website', name: 'Website', icon: Globe },
                { id: 'seo', name: 'SEO', icon: TrendingUp },
                { id: 'ads', name: 'Ads', icon: TrendingUp },
                { id: 'email', name: 'Email', icon: Mail },
                { id: 'design', name: 'Design', icon: Palette },
                { id: 'custom', name: 'Custom', icon: Settings },
              ].map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setActiveContentType(type.id as ContentType)}
                    className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                      activeContentType === type.id
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      {type.name}
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        {/* Content Area */}
        <div className="p-6">
          {/* Plans Tab */}
          {activeTab === 'plans' && (
            <div className="space-y-4">
              {filteredPlans.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No plans found
                </div>
              ) : (
                filteredPlans.map((plan) => (
                  <div key={plan.id} className="border border-gray-200 rounded-lg">
                    <div
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                      onClick={() => togglePlanExpansion(plan.id)}
                    >
                      <div className="flex items-center space-x-3">
                        {expandedPlans.has(plan.id) ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                        <div>
                          <h3 className="font-medium text-gray-900">{plan.name}</h3>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className="text-sm text-gray-600">
                              {plan.type === 'weekly' ? 'Weekly' : 'Monthly'}
                            </span>
                            <span className="text-sm text-gray-600">
                              {new Date(plan.start_date).toLocaleDateString()} - {new Date(plan.end_date).toLocaleDateString()}
                            </span>
                            <span className="text-sm text-gray-600">
                              {plan.item_count || 0} items
                            </span>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(plan.status)}`}>
                              {plan.status}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => {
                            setSelectedPlanForLinks(plan);
                            setShowGenerateLinksModal(true);
                          }}
                          className="p-2 text-blue-600 hover:text-blue-700"
                          title="Generate Review Links"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingPlan(plan);
                            setPlanForm({
                              name: plan.name,
                              type: plan.type,
                              start_date: plan.start_date,
                              end_date: plan.end_date,
                              client_id: plan.client_id?.toString() || '',
                              status: plan.status
                            });
                            setShowPlanModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePlan(plan.id)}
                          className="p-2 text-red-400 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Expanded Content Items */}
                    {expandedPlans.has(plan.id) && (
                      <div className="border-t border-gray-200">
                        {plan.items && plan.items.length > 0 ? (
                          <div className="p-4 space-y-2">
                            {plan.items.map((item) => (
                              <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900">{item.title}</h4>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {item.type} • {item.status}
                                  </p>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {getApprovalIcon(item.status)}
                                  <button
                                    onClick={() => handleDeleteContent(item.id)}
                                    className="text-red-400 hover:text-red-600"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ))}
                            <button
                              onClick={() => {
                                setContentForm({ ...contentForm, plan_id: plan.id.toString() });
                                setShowContentModal(true);
                              }}
                              className="w-full p-2 border border-dashed border-gray-300 rounded text-sm text-gray-600 hover:text-gray-800 hover:border-gray-400"
                            >
                              <Plus className="w-4 h-4 inline mr-1" />
                              Add Content to Plan
                            </button>
                          </div>
                        ) : (
                          <div className="p-8 text-center text-gray-500">
                            <p>No content items in this plan</p>
                            <button
                              onClick={() => {
                                setContentForm({ ...contentForm, plan_id: plan.id.toString() });
                                setShowContentModal(true);
                              }}
                              className="mt-3 text-blue-600 hover:text-blue-700 text-sm"
                            >
                              Add first content item
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Content Items Tab */}
          {activeTab === 'content' && (
            <div className="space-y-4">
              {filteredContent.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No content items found
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assets
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredContent.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{item.title}</div>
                          {item.description && (
                            <div className="text-sm text-gray-500">{item.description}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{item.type}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(item.status)}`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{item.asset_count || 0}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => {
                                setEditingContent(item);
                                setContentForm({
                                  title: item.title,
                                  type: item.type,
                                  description: item.description || '',
                                  status: item.status,
                                  client_id: item.client_id?.toString() || '',
                                  plan_id: item.plan_id?.toString() || ''
                                });
                                setShowContentModal(true);
                              }}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteContent(item.id)}
                              className="text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Assets Tab */}
          {activeTab === 'assets' && (
            <div>
              {filteredAssets.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No assets found
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredAssets.map((asset) => (
                    <div key={asset.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition">
                      <div className="aspect-w-16 aspect-h-9 bg-gray-100 rounded mb-3 flex items-center justify-center">
                        <Image className="w-8 h-8 text-gray-400" />
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 truncate">{asset.file_name}</h4>
                      <p className="text-xs text-gray-600 mt-1">{asset.asset_type}</p>
                      <div className="flex items-center justify-between mt-3">
                        <button className="text-blue-600 hover:text-blue-700">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600">
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAsset(asset.id)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        File Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Size
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Content ID
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAssets.map((asset) => (
                      <tr key={asset.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{asset.file_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{asset.asset_type}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">
                            {asset.file_size ? `${(asset.file_size / 1024).toFixed(2)} KB` : '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{asset.content_id || '-'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button className="text-blue-600 hover:text-blue-700">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="text-gray-400 hover:text-gray-600">
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAsset(asset.id)}
                              className="text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingPlan ? 'Edit Plan' : 'Create New Plan'}
            </h3>

            <form onSubmit={handleSavePlan} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={planForm.type}
                  onChange={(e) => setPlanForm({ ...planForm, type: e.target.value as 'weekly' | 'monthly' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <select
                  value={planForm.client_id}
                  onChange={(e) => setPlanForm({ ...planForm, client_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="">Select client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={planForm.start_date}
                    onChange={(e) => setPlanForm({ ...planForm, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                  <input
                    type="date"
                    value={planForm.end_date}
                    onChange={(e) => setPlanForm({ ...planForm, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClosePlanModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingPlan ? 'Update' : 'Create'} Plan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Content Modal */}
      {showContentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingContent ? 'Edit Content' : 'Create New Content'}
            </h3>

            <form onSubmit={handleSaveContent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={contentForm.title}
                  onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={contentForm.type}
                  onChange={(e) => setContentForm({ ...contentForm, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="website">Website</option>
                  <option value="social">Social Media</option>
                  <option value="email">Email</option>
                  <option value="design">Design</option>
                  <option value="google_ads">Google Ads</option>
                  <option value="facebook_ads">Facebook Ads</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={contentForm.description}
                  onChange={(e) => setContentForm({ ...contentForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                <select
                  value={contentForm.plan_id}
                  onChange={(e) => setContentForm({ ...contentForm, plan_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="">No plan</option>
                  {plans.map(plan => (
                    <option key={plan.id} value={plan.id}>{plan.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseContentModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingContent ? 'Update' : 'Create'} Content
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Links Modal */}
      {showGenerateLinksModal && selectedPlanForLinks && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Generate Review Links</h3>
            <p className="text-gray-600 mb-6">
              Generate review links for "{selectedPlanForLinks.name}"?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowGenerateLinksModal(false);
                  setSelectedPlanForLinks(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateLinks}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Generate Links
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Asset Creation Modal - Full Screen */}
      {showAssetCreation && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <div className="min-h-screen">
            {/* Header with Close Button */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-gray-900">
                Create {activeContentType.charAt(0).toUpperCase() + activeContentType.slice(1)} Content
              </h2>
              <button
                onClick={() => {
                  setShowAssetCreation(false);
                  loadData(); // Refresh data after creation
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Close
              </button>
            </div>

            {/* Render appropriate component based on content type */}
            <div className="p-6">
              {activeContentType === 'social' && <SocialAssetCreation />}
              {activeContentType === 'website' && <WebsiteAssetCreation />}
              {activeContentType === 'seo' && <SEOAssetCreation />}
              {activeContentType === 'ads' && <AdsAssetCreation />}
              {activeContentType === 'email' && <EmailAssetCreation />}
              {activeContentType === 'design' && <DesignAssetCreation />}
              {activeContentType === 'custom' && <CustomAssetCreation />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}